import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { openProfileDrawer } from './profileDrawerHelper';
import DealMenu from './DealMenu';
import Loading from '../../common/Loading';
import DealInterestCard from './DealInterestCard';
import DealInterestListItem from './DealInterestListItem';
import { S3Image, getAvatarStyle } from '../../../lib/s3';

const GET_COMPANY_SETTINGS = gql`
  query GetCompanySettings {
    company {
      id
      agent_word
      agent_word_fr
      avatar_background_color
      avatar_directory
      primary_color
    }
  }
`;

// Error boundary to catch and handle runtime errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in DealNegotiate component:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.error_boundary.title" />
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.error_boundary.description" />
                </p>
              </div>
              <div className="mt-4">
                <button 
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.error_boundary.refresh_button" />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const DealNegotiate = ({ deal, refetchDeal, currentUser, activeFilter = 'all' }) => {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('grid');
  const { data: settingsData } = useQuery(GET_COMPANY_SETTINGS, {
    fetchPolicy: 'cache-first'
  });
  
  const companyData = settingsData?.company || currentUser?.company || {};
  const companySettings = {
    avatar_directory: companyData.avatar_directory,
    avatar_background_color: companyData.avatar_background_color,
    primary_color: companyData.primary_color,
  };
  const agentWord = (intl.locale === 'fr' ? (companyData.agent_word_fr || 'conseiller') : (companyData.agent_word || 'advisor')).toLowerCase();
  const agentWordLabel = agentWord.charAt(0).toUpperCase() + agentWord.slice(1);
  const agentWordLower = agentWord.toLowerCase();
  const agentWordPlural = agentWord.endsWith('s') ? agentWord : `${agentWord}s`;
  const agentWordPluralLower = agentWordPlural.toLowerCase();
  const agentWordPluralLabel = agentWordPlural.charAt(0).toUpperCase() + agentWordPlural.slice(1);

  // Show loading state if deal is undefined
  if (!deal) {
    return <Loading message={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_negotiate.loading_message' })} />;
  }
  
  // Safely access negotiating interests - check both evaluate property and negotiate property
  // as the field name might be different based on the GraphQL schema
  let negotiatingDealInterests = [];
  
  if (deal.evaluate) {
    // Filter from evaluate field (all deal interests)
    negotiatingDealInterests = deal.negotiating.filter(
      interest => interest.workflow_state === "negotiating"
    );
  } else if (deal.negotiate) {
    // Or use dedicated field if available
    negotiatingDealInterests = Array.isArray(deal.negotiate) ? deal.negotiate : [];
  } else {
    // Fallback to number of negotiations count
    negotiatingDealInterests = [];
  }

  // Apply search filtering if there's a search term
  const filteredNegotiating = () => {
    if (!negotiatingDealInterests.length) return [];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return negotiatingDealInterests.filter(
        interest => interest?.inventory_user?.user && (
          (interest.inventory_user.user.name || '').toLowerCase().includes(term) ||
          (interest.inventory_user.user.city || '').toLowerCase().includes(term) ||
          (interest.inventory_user.user.province || '').toLowerCase().includes(term)
        )
      );
    }
    
    return negotiatingDealInterests;
  };
  
  const dealName = deal.deal_name || '';
  const dealId = deal.id || '';
  const negotiating = filteredNegotiating();

  return (
    <div>
      {/* Search and View Options */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 md:mb-0">
          {intl.formatMessage(
            {
              id: 'succession_deals.wizard.steps.deal_negotiate.title',
              defaultMessage: 'Negotiating {agentWordPlural} ({count})'
            },
            { agentWordPlural: agentWordPluralLabel, count: negotiating.length }
          )}
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="relative" style={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px' }}>
            <input
              type="text"
              placeholder={intl.formatMessage(
                { id: 'succession_deals.wizard.steps.deal_negotiate.search_placeholder' },
                { agent_word: agentWordLabel }
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                borderRadius: '9999px',
                backgroundColor: 'var(--succession-bg-secondary, #f8fafc)',
                border: '1px solid var(--succession-border, #64748b)',
                color: 'var(--succession-text-primary, #0f172a)',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: 'none',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--succession-primary, #5489B6)';
                e.target.style.boxShadow = '0 0 0 3px rgba(84, 137, 182, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--succession-border, #64748b)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div className="absolute left-3 top-3 text-succession-text-muted" style={{ pointerEvents: 'none' }}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex border border-succession-border rounded-lg overflow-hidden flex-shrink-0">
            <button
              onClick={() => setView('grid')}
              className={`px-4 py-2 flex items-center ${view === 'grid' ? 'bg-succession-primary-light text-succession-primary' : 'text-succession-text-secondary'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.view_options.grid" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 flex items-center ${view === 'list' ? 'bg-succession-primary-light text-succession-primary' : 'text-succession-text-secondary'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.view_options.list" />
            </button>
          </div>
        </div>
      </div>

      {/* Stage description */}
      <div className="bg-succession-primary-light border-l-4 border-succession-primary rounded-md p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-succession-primary" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <div className="mt-2 text-sm text-succession-primary">
              <p>
                <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.stage_description.title" />
                &nbsp;
                {negotiating.length === 0 ? (
                  <FormattedMessage
                    id="succession_deals.wizard.steps.deal_negotiate.stage_description.no_negotiations"
                    values={{ agent_word: agentWordLabel }}
                  />
                ) : (
                  <FormattedMessage
                    id="succession_deals.wizard.steps.deal_negotiate.stage_description.negotiations_count_with_agent_word"
                    defaultMessage="You have {count} {agentWordPlural} negotiating."
                    values={{
                      count: negotiating.length,
                      agentWordPlural: agentWordPluralLower
                    }}
                  />
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {negotiating.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            {intl.formatMessage(
              {
                id: 'succession_deals.wizard.steps.deal_negotiate.empty_state.title_with_agent_word',
                defaultMessage: 'No {agentWordPlural} in negotiation'
              },
              { agentWordPlural: agentWordPluralLower }
            )}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {intl.formatMessage(
              {
                id: 'succession_deals.wizard.steps.deal_negotiate.empty_state.description_with_agent_word',
                defaultMessage: '{agentWordPlural} in the negotiation phase will appear here.'
              },
              { agentWordPlural: agentWordPluralLabel }
            )}
          </p>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {view === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {negotiating.map((interest) => (
                <DealInterestCard
                  key={interest.id}
                  dealInterest={interest}
                  dealName={dealName}
                  companySettings={companySettings}
                  onViewDetails={() => {
                    if (interest?.inventory_user?.user?.fb_user_name) {
                      openProfileDrawer(interest.inventory_user.user.fb_user_name, { fromDeal: true, dealInterest: interest });
                    }
                  }}
                  scheduleMeetingPath={`/succession/deals/${dealName}/schedule-meeting/${interest.id}`}
                  badgeText={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_negotiate.status.negotiating' })}
                  badgeColor="purple"
                  viewDetailsLabel={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_negotiate.actions.view_profile' })}
                />
              ))}
            </div>
          )}
          
          {/* List View */}
          {view === 'list' && (
            <div className="overflow-x-auto border border-succession-border rounded-lg">
              <table className="min-w-full divide-y divide-succession-border">
                <thead className="bg-succession-bg-secondary">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider w-80">
                      {agentWordLabel}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.table_headers.location" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.table_headers.status" />
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">
                        <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.table_headers.actions" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
                  {negotiating.map(interest => {
                    const user = interest?.inventory_user?.user;
                    if (!user) return null;
                    const avatarStyle = getAvatarStyle(user.profile_picture, companySettings);
                    
                    return (
                      <tr key={interest.id} className="hover:bg-succession-hover-bg">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className="h-10 w-10 rounded-full flex items-center justify-center text-gray-500 text-sm font-semibold overflow-hidden"
                              style={{ backgroundColor: avatarStyle.backgroundColor }}
                            >
                              {user.profile_picture ? (
                                <img 
                                  src={avatarStyle.url}
                                  alt={user.name}
                                  className="h-full w-full object-cover"
                                  style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                                />
                              ) : (
                                user?.name ? user.name.charAt(0).toUpperCase() : '?'
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user?.name || intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_negotiate.fallback.unknown_advisor' })}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user?.city ? `${user.city}${user.province ? `, ${user.province}` : ''}` : 
                            intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_negotiate.fallback.no_location' })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.status.negotiating" />
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link 
                            to={`/succession/deals/${dealName}/schedule-meeting/${interest.id}`} 
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                          >
                            <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.actions.schedule_meeting" />
                          </Link>
                          <button
                            onClick={() => {
                              if (user?.fb_user_name) {
                                openProfileDrawer(user.fb_user_name, { fromDeal: true, dealInterest: interest });
                              }
                            }}
                            className="text-indigo-600 hover:text-indigo-900 bg-transparent border-none cursor-pointer"
                          >
                            <FormattedMessage id="succession_deals.wizard.steps.deal_negotiate.actions.view_profile" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

DealNegotiate.propTypes = {
  deal: PropTypes.object,
  refetchDeal: PropTypes.func.isRequired
};

// Wrap component with error boundary
export default function DealNegotiateWithErrorBoundary(props) {
  return (
    <ErrorBoundary>
      <DealNegotiate {...props} />
    </ErrorBoundary>
  );
} 